import React from 'react';

import type { CountingCollectionPayload } from '../../core/types/payloads/counting_collection.types';
import { CountingItemIcon } from './countingCollectionIcons';

export interface CountingCollectionViewProps {
  payload: CountingCollectionPayload;
  answers?: Record<string, string>;
  interactive?: boolean;
  onAnswerChange?: (groupId: string, answer: string) => void;
}

function CountPromptBox({
  groupId,
  answer,
  interactive,
  onAnswerChange,
}: {
  groupId: string;
  answer?: string;
  interactive?: boolean;
  onAnswerChange?: (groupId: string, answer: string) => void;
}) {
  if (interactive) {
    return (
      <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3 print:border-[#e2e8f0]">
        <span className="text-sm font-medium text-slate-700">¿Cuántos hay?</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          aria-label="¿Cuántos hay?"
          value={answer ?? ''}
          onChange={(event) => onAnswerChange?.(groupId, event.target.value)}
          className="h-9 w-14 rounded-md border border-slate-300 bg-white text-center text-sm font-semibold text-slate-800 shadow-sm print:border-[#cbd5e1]"
        />
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3 print:border-[#e2e8f0]">
      <span className="text-sm font-medium text-slate-700">¿Cuántos hay?</span>
      <span className="inline-flex h-9 min-w-[3.5rem] items-center justify-center rounded-md border border-dashed border-slate-300 bg-white px-2 text-sm font-semibold text-slate-400 print:border-[#cbd5e1]">
        {answer?.trim() ? answer : ''}
      </span>
    </div>
  );
}

export function CountingCollectionView({
  payload,
  answers = {},
  interactive = false,
  onAnswerChange,
}: CountingCollectionViewProps) {
  const instruction = String(payload.instruction || '').trim();

  return (
    <article className="counting-collection rounded-xl border border-slate-100 bg-[#F6F5FA]/60 p-6 print:border-[#ddd] print:bg-white">
      {instruction ? (
        <p className="counting-collection__instruction mb-5 text-sm font-medium text-slate-600 print:text-slate-700">
          {instruction}
        </p>
      ) : null}

      <div className="counting-collection__groups grid gap-4 sm:grid-cols-2">
        {payload.groups.map((group, groupIndex) => (
          <section
            key={group.id}
            className="counting-collection__group rounded-xl border border-slate-100 bg-slate-50/50 p-4 print:border-[#e2e8f0] print:bg-[#f8fafc]"
          >
            {group.label ? (
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {group.label}
              </h3>
            ) : null}

            <div
              className="counting-collection__items flex flex-wrap gap-2"
              aria-label={`Grupo con ${group.count} elementos`}
            >
              {Array.from({ length: group.count }, (_, index) => (
                <CountingItemIcon
                  key={`${group.id}-${index}`}
                  itemType={group.itemType}
                  colorIndex={groupIndex}
                />
              ))}
            </div>

            <CountPromptBox
              groupId={group.id}
              answer={answers[group.id]}
              interactive={interactive}
              onAnswerChange={onAnswerChange}
            />
          </section>
        ))}
      </div>
    </article>
  );
}
